/**
 * The feed: everything that has happened in one exploration, in the order it
 * happened — the passes, the runs that scheduled them, and the reader's own
 * questions — as one list a column can render like a conversation.
 *
 * DERIVED, NEVER RECORDED. The session already keeps the whole story:
 * `session.ops` is every pass with its start, its end, its full reasoning trace
 * and what it produced, and `session.runs` joins the autonomous ones to the
 * moves that chose them. A second event log written beside that record would be
 * a copy, and this codebase's scar tissue about copies is extensive — see the
 * refutation gate that existed twice. So this module holds no state at all: it
 * is a sort, a join, and the few judgements below, and it can be handed the
 * same session twice and say the same thing twice.
 *
 * A RUN IS ONE ITEM, not a stretch of the list. The first feed rendered every
 * pass of a run as its own message between start and end markers, and the demo
 * proved the failure mode in one sitting: a 24-move run writes a wall of
 * transcript between the reader and their last question. A conversation treats
 * sustained work the way a chat treats a working partner — one message that
 * grows — so a run's passes ride INSIDE its item and the surface decides how
 * much of them to show. Passes made by hand (a verb press, the demo's authored
 * unfurl) have no run and stay top-level messages.
 *
 * The questions are the one input the session cannot supply, because a
 * conversation is deliberately not reachable from the session document
 * (`core/conversations.ts`). They arrive as an argument, in the caller's own
 * shape, and only the fields named here are read.
 *
 * The SEED opens it (D359): the words the reader sent first, read off
 * `session.seed`, which is never edited — a rewrite of the root changes the claim
 * on the card, not what was sent — so the conversation's first message says
 * what was sent, as a chat's history does.
 *
 * The standing brief's changes ride here too, each where it fell between the
 * passes — a directive added on the third day, one reworded, one removed — so
 * the thread says when what every later pass was told changed. They are read
 * off the dates the brief already keeps (`briefChanges` in `./brief`), which is
 * the same rule as the rest: derived, never recorded here.
 *
 * Pure and a LEAF, like `thread.ts`: runtime imports from `./types` and `./brief`,
 * itself a leaf over `./types`, so the UI and the store can both read it without
 * a cycle.
 */

import type {
  Audit,
  FieldSweep,
  Op,
  PanelCheck,
  PriorArtSearch,
  ReasonNode,
  Run,
  RunMove,
  Session,
  Verification,
  VerificationArchiveEntry,
} from './types'
import type { StoredAbout } from './conversations'
import { briefChanges, type BriefChange } from './brief'

/** One question in the feed, structurally — the engine's `ChatTurn` goes
 *  straight in, with the two fields the stored form added riding along. */
export interface FeedTurn {
  id: string
  question: string
  answer: string
  live?: boolean
  error?: string
  at?: number
  about?: StoredAbout
  /** The model speaking unprompted — the narrator's turns carry no question. */
  voice?: true
  /** Passes the reply proposed, as loose data — the surface that renders a
   *  chip re-validates every row against its own tables (`ui/actPress.ts`). */
  acts?: Array<{ kind: string; node: string; why?: string }>
  /** While live: the transport waiting to ask again. Never stored. */
  waiting?: string
}

/**
 * What a pass is doing on screen.
 *
 * `died` is the third state and the reason this is not a boolean: an op with no
 * `finishedAt` is usually a tab that closed mid-pass — the absence is the
 * information, `Op.finishedAt`'s own rule — and rendering it as live would show
 * a pass forever thinking about a request that no longer exists.
 */
export type PassState = 'live' | 'done' | 'died'

/** One pass as an item carries it — the op, its screen state, and the run move
 *  that chose it where a run did. */
export interface PassEntry {
  op: Op
  state: PassState
  move?: RunMove
}

export type FeedItem =
  | { kind: 'seed'; at: number; text: string }
  | { kind: 'brief'; at: number; change: BriefChange }
  | { kind: 'run'; at: number; run: Run; passes: PassEntry[] }
  | { kind: 'pass'; at: number; op: Op; state: PassState; move?: RunMove }
  | { kind: 'turn'; at: number; turn: FeedTurn }

/**
 * When a turn happened, with the fallback chain written once. Stored turns from
 * before the feed carry no `at`; their ids have always been minted as
 * `t<Date.now()>` (`sendOn`), so the timestamp is usually recoverable from the
 * name — and zero, the last resort, sorts an undatable turn to the top rather
 * than interleaving it somewhere it claims to belong.
 */
export function turnAt(t: Pick<FeedTurn, 'id' | 'at'>): number {
  if (t.at !== undefined) return t.at
  const m = /^t(\d+)$/.exec(t.id)
  return m ? Number(m[1]) : 0
}

/**
 * A finished check's own result, read off its target — or nothing.
 *
 * A verification is append-only now, so a re-check can recover the exact old
 * row by `opId`, including from the session archive after its claim card was
 * removed. Stored rows from before that join existed fall back to the timestamp
 * discriminator panels and audits still use. The slack absorbs the verify path's
 * write order, where the record is patched onto the node a beat AFTER `endOp`
 * stamps the finish (`reason.ts`); an unfinished op admits nothing, because a
 * result cannot belong to a pass that has not ended.
 */
export function passCheckResults(
  op: Op,
  target: ReasonNode | undefined,
  archive: readonly VerificationArchiveEntry[] = [],
  sweeps: readonly FieldSweep[] = [],
): {
  verification?: Verification
  panel?: PanelCheck
  audit?: Audit
  priorArt?: PriorArtSearch
  sweep?: FieldSweep
} {
  const within = (at: number) =>
    op.finishedAt !== undefined && at >= op.startedAt && at <= op.finishedAt + 5000
  const checks = [
    ...(target?.verificationHistory ?? []),
    ...(target?.verification ? [target.verification] : []),
    ...(op.targetIds.length === 1
      ? archive.filter((row) => row.nodeId === op.targetIds[0]).map((row) => row.verification)
      : []),
  ]
  const verification =
    op.kind === 'verify' && op.finishedAt !== undefined
      ? (checks.find((row) => row.opId === op.id) ??
        checks.find((row) => row.opId === undefined && within(row.checkedAt)))
      : undefined
  return {
    ...(verification ? { verification } : {}),
    ...(op.kind === 'panel' && target?.panel && within(target.panel.checkedAt)
      ? { panel: target.panel }
      : {}),
    ...(op.kind === 'audit' && target?.audit && within(target.audit.at)
      ? { audit: target.audit }
      : {}),
    // A prior-art search names its op on the record it wrote, so the join is
    // exact and needs no clock: the conjecture keeps every search it has had.
    ...(op.kind === 'priorArt' && priorArtOf(op, target)
      ? { priorArt: priorArtOf(op, target) }
      : {}),
    // A sweep lives on the session, not on its node, and names its op too.
    ...(op.kind === 'sweep' && sweeps.some((s) => s.opId === op.id)
      ? { sweep: sweeps.find((s) => s.opId === op.id) }
      : {}),
  }
}

const priorArtOf = (op: Op, target: ReasonNode | undefined): PriorArtSearch | undefined =>
  target?.conjecture?.priorArt?.find((p) => p.opId === op.id)

/**
 * The check that has since replaced a pass's own on the same claim — what the
 * record reads now — or null while the pass's check is still the current one.
 *
 * A pass's message keeps its own finding (`passCheckResults`), which is right for
 * a history and was wrong for a reader skimming it: a refutation a later check
 * overturned read, on its row, exactly as a standing one does. The row says what
 * the record reads now beside what it read then. Matched by `opId`, and by the
 * stamp for rows stored before checks carried one.
 */
export function checkedAgainSince(
  target: ReasonNode | undefined,
  own: Verification | undefined,
): Verification | null {
  const now = target?.verification
  if (!now || !own) return null
  const same =
    own.opId !== undefined || now.opId !== undefined
      ? own.opId === now.opId
      : own.checkedAt === now.checkedAt && own.reading === now.reading
  return same ? null : now
}

/**
 * Ties broken by role, not left to chance: a run's item opens before a lone
 * pass that started the same millisecond, and a question about either reads
 * after it. `Array.prototype.sort` is stable, so equal keys otherwise keep the
 * order they were pushed — ops and runs in their own recorded order. A change to
 * the brief goes first: a pass that started the millisecond a directive joined
 * was briefed with it, as `briefLateNote` counts. The seed is never sorted —
 * `feedItems` puts it at the head — and its entry here only completes the table.
 */
const KIND_ORDER: Record<FeedItem['kind'], number> = {
  seed: -1,
  brief: 0,
  run: 1,
  pass: 2,
  turn: 3,
}

/**
 * The feed for one exploration.
 *
 * `busy` is the store's own pass lock, and it is what separates `live` from
 * `died`: only the NEWEST unfinished op can be the pass in flight, because
 * `beginOp` appends and the lock admits one pass at a time — an older
 * unfinished op is one some session never came back to. The rule is the same
 * whether the pass rides a run item or stands alone.
 *
 * A NARRATION IS NOT A PASS, and it is left out of both the rows and that rule.
 * Its op is the receipt D91 gave the voice call — banked before the call, so an
 * interrupted one is still on the ledger — and the narrator never takes the
 * pass lock. So for the six to nine seconds the voice streams after every pass,
 * its op was unfinished with `busy` false, and the feed printed "This pass never
 * finished — the tab closed under it" under a pass that had finished and a tab
 * that was open. Its rendering is the voice turn, glued under the pass it
 * narrates (D51) — which it now actually sits under, rather than under a row of
 * its own — and its receipt stays where receipts are read, on the ledger. Where
 * this browser holds no voice turn for it, `recordedNarrations` makes one from
 * the record, and it arrives here among `turns`. Left
 * out of `newest` too: a queued narration can start while a pressed pass is in
 * flight, and its row, appended after, would have made the real pass read as
 * one the tab had closed under.
 *
 * THE SEED IS FIRST BY DEFINITION, not by a date. It is the message every
 * other one answers, so it is put at the head after the sort rather than sorted
 * in on `createdAt` — the one time in a stored document read as written while
 * every other is translated through the save clock (D79), so a document saved
 * with a clock ahead would date its own seed after its first pass. Its `at` is
 * the first item's, which is all a row needs from it. Absent when the caller has
 * no seed to give, or a blank one, and a blank canvas has no feed to open.
 *
 * Takes the session fields by name rather than the session, so the signature
 * says exactly what the feed is built from — and so a test can hand it two
 * arrays without building a graph.
 */
export function feedItems(
  session: Pick<Session, 'ops' | 'runs' | 'brief' | 'formerDirectives'> &
    Partial<Pick<Session, 'seed'>>,
  turns: readonly FeedTurn[],
  busy: boolean,
): FeedItem[] {
  const items: FeedItem[] = []

  for (const change of briefChanges(session)) items.push({ kind: 'brief', at: change.at, change })

  // The joins from a pass to its run: the move that chose it, and the run item
  // its entry rides in. First writer wins on the off chance of a duplicate
  // opId — moves are recorded once per logical row, and a portfolio's several
  // rows share one op, where the first row is the one that names why the run
  // went there.
  const moveByOp = new Map<string, RunMove>()
  const runOfOp = new Map<string, Run>()
  for (const r of session.runs) {
    for (const m of r.moves) {
      if (!moveByOp.has(m.opId)) moveByOp.set(m.opId, m)
      if (!runOfOp.has(m.opId)) runOfOp.set(m.opId, r)
    }
  }

  const runItems = new Map<string, Extract<FeedItem, { kind: 'run' }>>()
  for (const run of session.runs) {
    const item: Extract<FeedItem, { kind: 'run' }> = {
      kind: 'run',
      at: run.startedAt,
      run,
      passes: [],
    }
    runItems.set(run.id, item)
    items.push(item)
  }

  const passes = session.ops.filter((op) => !op.narration)
  const newest = passes.at(-1)
  for (const op of passes) {
    const state: PassState =
      op.finishedAt !== undefined ? 'done' : busy && op === newest ? 'live' : 'died'
    const entry: PassEntry = { op, state, move: moveByOp.get(op.id) }
    const owner = runOfOp.get(op.id)
    // `session.ops` is append-order, so each run's passes arrive already in the
    // order they ran — no second sort inside the item.
    if (owner) runItems.get(owner.id)!.passes.push(entry)
    else items.push({ kind: 'pass', at: op.startedAt, ...entry })
  }

  for (const t of turns) items.push({ kind: 'turn', at: turnAt(t), turn: t })

  items.sort((a, b) => a.at - b.at || KIND_ORDER[a.kind] - KIND_ORDER[b.kind])
  const seed = session.seed?.trim()
  if (seed) items.unshift({ kind: 'seed', at: items[0]?.at ?? 0, text: seed })
  return items
}

/**
 * How far apart a voice turn and its narration's receipt can be minted and still
 * be one narration. The narrator mints both in one synchronous stretch (`speakOn`,
 * then the op), so they are milliseconds apart; the slack is for a busy tab, and
 * it stays well under the seconds any one narration takes to speak — the voice
 * is one at a time, so the next one cannot start inside it.
 */
export const NARRATION_TURN_SLACK_MS = 250

/**
 * The narrations the RECORD holds and this browser's conversation does not, as
 * the voice turns the feed already knows how to render.
 *
 * A narration is two things (D91): a receipt in `session.ops`, which travels
 * with the exploration, and a voice turn in the per-browser conversation cache
 * (`core/conversations.ts`), which never does. A copy opened from a link, or the
 * sender's own exploration opened on another device, had the receipts and showed
 * none of their words — `feedItems` leaves narration ops out, rightly, because
 * they are not passes. These are those words, at their place in time, in the
 * turn's own shape, so the feed renders them under the same name as the live
 * voice (`NARRATION_LABEL`).
 *
 * NEVER A NARRATION TWICE. A receipt is left out when a voice turn in `turns`
 * is the same narration: minted within `NARRATION_TURN_SLACK_MS` of it, or
 * carrying exactly its words — the second join is for a document whose clock was
 * translated on the way in (`storedTime`), which moves the op and not the turn.
 * A narration still speaking has no finished receipt, so its live turn is alone.
 *
 * `said` reads a receipt's words (`narrationWords`), handed in so this module
 * stays a leaf. No proposals ride these turns: the chips a narration offered
 * were offers to whoever held the exploration then, and the trailer that named
 * them never reached a reader.
 */
export function recordedNarrations(
  session: Pick<Session, 'ops' | 'nodes' | 'seed'>,
  turns: readonly FeedTurn[],
  said: (op: Op) => string | null,
): FeedTurn[] {
  const voices = turns.filter((t) => t.voice)
  const out: FeedTurn[] = []
  for (const op of session.ops) {
    if (!op.narration) continue
    const words = said(op)
    if (!words) continue
    const covered = voices.some(
      (t) => t.answer === words || Math.abs(turnAt(t) - op.startedAt) <= NARRATION_TURN_SLACK_MS,
    )
    if (covered) continue
    // What it was about, the narrator's own rule (`opAbout`): the claim its pass
    // worked on, or the whole exploration for the seed and for a run's wrap-up.
    const target = op.targetIds[0] ? session.nodes[op.targetIds[0]] : undefined
    const seed = session.seed.trim()
    const about: StoredAbout =
      target && target.parentId !== null
        ? { kind: 'node', id: target.id, label: target.label }
        : {
            kind: 'graph',
            label: seed.length > 80 ? `${seed.slice(0, 79)}…` : seed || 'This exploration',
          }
    out.push({ id: op.id, question: '', answer: words, at: op.startedAt, voice: true, about })
  }
  return out
}

/* ── proposals ────────────────────────────────────────────────────────────
   The chips a reply proposed are stored on its turn and re-validated at every
   render. Two further questions are asked of them here, both of the RECORD and
   neither of the model's words: which turn's proposals are still the live ones,
   and whether a proposed verb has already run on its target since. */

/** One proposal, structurally — `FeedTurn['acts']`'s row. */
type Proposal = NonNullable<FeedTurn['acts']>[number]

/**
 * The one turn whose proposals stay pressable: the newest finished turn that
 * still proposes something the surface can render.
 *
 * Ten passes in, every narration had left its two chips standing, so the thread
 * carried a dozen live Run buttons — some for checks already done, all of them
 * priced as if the graph were the one each was written against. A proposal is
 * a suggestion for what to do NEXT — an answer's, when the reader asked for a
 * move, or a voice turn's stored from before the voice stopped naming one
 * (D295) — and "next" is only ever the newest one; older rows stay on screen as
 * what was proposed, and stop being buttons.
 *
 * `renderable` is the surface's own re-validation — the kind against its act
 * vocabulary, the node against the graph as it stands — handed in rather than
 * restated, so this leaf keeps importing nothing but types. A turn whose every
 * proposal has since gone stale (its nodes pruned) is not the live one: it would
 * render nothing, and the row before it would then have no reason to fold.
 */
export function liveProposalTurn(
  turns: readonly FeedTurn[],
  renderable: (act: Proposal) => boolean,
): string | null {
  for (let i = turns.length - 1; i >= 0; i--) {
    const t = turns[i]!
    if (t.live || t.error || !t.acts?.length) continue
    if (t.acts.some(renderable)) return t.id
  }
  return null
}

/** What a proposal's verb has done on its target since it was proposed — or is
 *  waiting to do, on the Up next line behind the pass in flight (D162). */
export type ProposalState = 'open' | 'running' | 'upNext' | 'done'

/** The six moves a run can pack into one portfolio pass, whose parts each name
 *  the verb and the target a whole pass would have carried alone. */
const PORTFOLIO_VERBS: ReadonlySet<string> = new Set([
  'expand',
  'deepen',
  'challenge',
  'diverge',
  'experiment',
  'invert',
])

/**
 * Whether a proposed verb has already run on its target since the proposal was
 * made — read off the stored ops, the way the ledger and the run account read
 * them, and never off anything the model said.
 *
 * A pass kind is its op kind: every act that runs a model (the six moves and the
 * three checks) records an op of the same name targeting the node, or a part of
 * a run's portfolio pass naming both. It counts only when it FINISHED cleanly —
 * an error or a refusal is a pass that did not do the thing, and the proposal
 * stays pressable so it can be tried again — and only when it started at or
 * after the proposal, so a turn is never marked done by the pass it was
 * narrating. A narration's own op is not a pass (D121) and is never read.
 *
 * The two free forms run no model and leave no op, so they read the record they
 * write instead: a result recorded on the node since, or a rewrite since.
 *
 * `running` is the one live state: the newest pass, still going, holding the
 * store's lock (`feedItems`' own rule for a live pass). `upNext` is the one
 * promised state: this verb on this node pressed under the lock and waiting on
 * the line (`core/upNext.ts`) — from this chip or from any other door, since it
 * is the same press and a second one would be refused as already up next. A
 * chip that came back asking again ("Run it?") is not waiting, so it does not
 * count. Passed in rather than read, in the line's own shape, so this stays a
 * leaf.
 */
export function proposalState(
  session: Pick<Session, 'ops' | 'nodes'>,
  act: Proposal,
  since: number,
  busy: boolean,
  upNext: readonly { verb: string; nodeId: string; held?: string }[] = [],
): ProposalState {
  if (act.kind === 'record' || act.kind === 'edit') {
    const node = session.nodes[act.node]
    const at = act.kind === 'record' ? node?.outcome?.recordedAt : node?.editedAt
    return at !== undefined && at >= since ? 'done' : 'open'
  }
  const passes = session.ops.filter((op) => !op.narration)
  const newest = passes.at(-1)
  let state: ProposalState = 'open'
  for (const op of passes) {
    if (op.startedAt < since) continue
    const carries =
      (op.kind === act.kind && op.targetIds.includes(act.node)) ||
      (op.kind === 'portfolio' &&
        PORTFOLIO_VERBS.has(act.kind) &&
        (op.portfolio?.parts ?? []).some((p) => p.kind === act.kind && p.targetId === act.node))
    if (!carries) continue
    if (op.finishedAt !== undefined) {
      if (!op.error && !op.refused) return 'done'
    } else if (busy && op === newest) {
      state = 'running'
    }
  }
  if (
    state === 'open' &&
    upNext.some((i) => !i.held && i.verb === act.kind && i.nodeId === act.node)
  )
    return 'upNext'
  return state
}
